import type {
  StayMapOption,
  StayPhoto
} from '../client'
import type {
  OptionSetLoadFailure
} from '../utils/optionSetLoad'
import {
  CookieConsentBanner
} from './CookieConsentBanner'
import {
  OptionSetUnavailable
} from './OptionSetUnavailable'

// Pieces shared by the stays options page and its confirmation page.

// @ts-ignore - Vite env types
export const MAPS_API_KEY = (import.meta.env?.VITE_GOOGLE_MAPS_API_KEY as string | undefined) || ''

export const CENTERED_SCREEN = 'mx-auto flex min-h-screen max-w-md flex-col items-center justify-center px-6 text-center'

export const ALREADY_PICKED = 'You\'ve already picked one of these — check your chat to keep going.'

export function PageShell({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div className="min-h-screen bg-catch-white text-catch-black font-sans">
      {children}
      <CookieConsentBanner storageKey="stays_cookie_consent" />
    </div>
  )
}

// The screen shown while the option set loads, or when it could not be read.
export function OptionSetStatus({
  state,
  onRetry
}: {
  state: 'loading' | OptionSetLoadFailure
  onRetry: () => void
}): JSX.Element {
  return (
    <PageShell>
      {state === 'loading'
        ? (
          <div className="flex min-h-screen items-center justify-center text-gray-500">
            Loading your options…
          </div>
          )
        : <OptionSetUnavailable failure={state} onRetry={onRetry} className={CENTERED_SCREEN} />}
    </PageShell>
  )
}

function googlePhotoUrl(photo: StayPhoto, width: number): string | null {
  if (!MAPS_API_KEY) {
    return null
  }
  // Place Photos (New) media endpoint; loaded client-side with the browser key.
  return `https://places.googleapis.com/v1/${photo.name}/media?maxWidthPx=${width}&key=${MAPS_API_KEY}`
}

// Prefer Google's place photo (with required attribution) over the LiteAPI
// image; fall back to a placeholder when neither is available.
export function HotelImage({
  option,
  width,
  wrapperClass
}: {
  option: StayMapOption
  width: number
  wrapperClass: string
}): JSX.Element {
  const googlePhoto = option.google_photos?.[0]
  const googleUrl = googlePhoto ? googlePhotoUrl(googlePhoto, width) : null
  const src = googleUrl ?? option.main_photo ?? null

  if (!src) {
    return (
      <div className={`flex items-center justify-center bg-catch-beige text-2xl ${wrapperClass}`}>
        🏨
      </div>
    )
  }
  return (
    <div className={`relative overflow-hidden ${wrapperClass}`}>
      <img src={src} alt={option.name} loading="lazy" className="h-full w-full object-cover" />
      {googleUrl && googlePhoto?.attribution && (
        <span
          title={`© ${googlePhoto.attribution}`}
          className="absolute inset-x-0 bottom-0 block truncate bg-black/40 px-1 text-[9px] leading-tight text-white"
        >
          © {googlePhoto.attribution}
        </span>
      )}
    </div>
  )
}
